import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Warning,
  WarningCircle,
  Info,
} from '@phosphor-icons/react';
import type { RemittanceDetail } from '@erp/rpc';
import { T } from 'gt-react';

type VerificationResult = NonNullable<RemittanceDetail['verificationResult']>;
type Finding = VerificationResult['findings'][number];

interface RemittanceVerificationPanelProps {
  verificationResult: RemittanceDetail['verificationResult'];
}

const SEVERITY_STYLES: Record<
  Finding['severity'],
  { icon: typeof Info; className: string }
> = {
  critical: {
    icon: WarningCircle,
    className: 'border-red-200 bg-red-50 text-red-800',
  },
  warning: {
    icon: Warning,
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  info: {
    icon: Info,
    className: 'border-slate-200 bg-slate-50 text-slate-700',
  },
};

function FindingRow({ finding }: { finding: Finding }) {
  const style = SEVERITY_STYLES[finding.severity];
  const Icon = style.icon;
  return (
    <div
      className={`flex gap-2 rounded-md border p-2 text-xs ${style.className}`}
    >
      <Icon className="mt-0.5 shrink-0" size={16} weight="fill" />
      <div className="space-y-0.5">
        <div className="font-medium">{finding.title}</div>
        <div className="opacity-90">{finding.detail}</div>
        {finding.reference && (
          <div className="font-mono text-[11px] opacity-70">
            <T context="ar remittance verification finding reference">Ref</T>:{' '}
            {finding.reference}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Surfaces the AI overseer's check of the extracted remittance against its
 * source document (persisted by the ingestion workflow). Renders nothing for
 * advices that were never verified.
 */
export function RemittanceVerificationPanel({
  verificationResult,
}: RemittanceVerificationPanelProps) {
  if (!verificationResult) return null;

  const { verdict, summary, findings, verifiedAt } = verificationResult;
  const isPass = verdict === 'pass';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">
            <T context="ar remittance verification panel title">Verification</T>
          </CardTitle>
          {isPass ? (
            <Badge
              variant="outline"
              className="border-green-200 bg-green-50 text-green-800"
            >
              <CheckCircle className="mr-1" size={14} weight="fill" />
              <T context="ar remittance verification verdict pass">Verified</T>
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-amber-200 bg-amber-50 text-amber-800"
            >
              <Warning className="mr-1" size={14} weight="fill" />
              <T context="ar remittance verification verdict needs review">
                Needs review
              </T>
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          <T context="ar remittance verification scope caption">
            Original extraction checked against the source document at ingest —
            not re-run after edits.
          </T>
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-sm text-muted-foreground">{summary}</div>
        {findings.length > 0 && (
          <div className="space-y-1.5">
            {findings.map((finding, i) => (
              <FindingRow
                key={`${finding.reference ?? finding.title}-${i}`}
                finding={finding}
              />
            ))}
          </div>
        )}
        <div className="text-[11px] text-muted-foreground">
          <T context="ar remittance verification checked timestamp">Checked</T>{' '}
          {new Date(verifiedAt).toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}
